import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import {
  Input, 
  Button, 
  Table, 
  Space, 
  Modal as AntModal, 
  Typography, 
  Card, 
  Select,
  message,
  Tooltip,
  Tag
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  ScheduleOutlined,
  SearchOutlined,
  CloseCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { sarabunFont } from '../../sarabunFont';
import './Classes.css';

const { Text } = Typography;
const { Option } = Select;
const { confirm } = AntModal;

// Function to load the Sarabun font into jsPDF
const loadSarabunFont = (doc) => {
  doc.addFileToVFS('Sarabun.ttf', sarabunFont.src.split('base64,')[1]);
  doc.addFont('Sarabun.ttf', 'Sarabun', 'normal');
  doc.setFont('Sarabun');
};

const FilterTaskTable = () => {
  const { t } = useTranslation();
  const [classGroups, setClassGroups] = useState([]);
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [popupTotalHours, setPopupTotalHours] = useState(0);
  const [editData, setEditData] = useState(null);
  const [registrationsCount, setRegistrationsCount] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [classIdToDelete, setClassIdToDelete] = useState(null);
  const [privateFilter, setPrivateFilter] = useState('all');
  const [startDateFilter, setStartDateFilter] = useState(null);
  const abortControllerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef(null);

  const itemsPerPage = 50;
  const API_URL = process.env.REACT_APP_API_URL;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}:${remainingMinutes.toString().padStart(2, '0')} ${t('hrs')}`;
  };

  const formatScheduleHour = (scheduleHourString) => {
    try {
      const [hours, minutes] = scheduleHourString.split(' ').map((value) => parseInt(value, 10));
      return `${hours}:${minutes.toString().padStart(2, '0')} ${t('hrs')}`;
    } catch (error) {
      console.error('Invalid schedule hour value:', scheduleHourString);
      return 'Invalid Time';
    }
  };

  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString);
    const daysOfWeek = t('daysOfWeek', { returnObjects: true });
    return daysOfWeek[date.getDay()];
  };

  const fetchClassGroups = useCallback(async (page, search, type, startDate) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const token = localStorage.getItem('token');
      const params = { page, size: itemsPerPage };
      if (search) params.search = search;
      if (type && type !== 'all') params.type = type;
      if (startDate) params.startDate = startDate.toISOString().split('T')[0];

      const response = await axios.get(`${API_URL}/class-groups`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        signal: controller.signal
      });

      setClassGroups(response.data.classGroups);
      setTotalItems(response.data.totalItems);
      if (loading) setLoading(false);
    } catch (error) {
      if (axios.isCancel(error)) return; // ignore aborted requests
      console.error('Error fetching class groups:', error);
      setError('Failed to fetch class groups. Please try again later.');
      if (loading) setLoading(false);
    }
  }, [API_URL, itemsPerPage, loading]);

  const fetchRegistrationsCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/registrations/registrations-count`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setRegistrationsCount(response.data);
    } catch (error) {
      console.error('Error fetching registrations count:', error);
      setError('Failed to fetch registrations count. Please try again later.');
    }
  }, [API_URL]);

  const fetchClassChangeLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      setClassChangeLogs(response.data);
  
    } catch (error) {
      console.error('Error fetching class change logs:', error);
    }
  }, [API_URL]);
  
  useEffect(() => {
    fetchClassGroups(currentPage, debouncedSearch, privateFilter, startDateFilter);
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchClassGroups, currentPage, debouncedSearch, privateFilter, startDateFilter]);

  useEffect(() => {
    fetchRegistrationsCount();
    fetchClassChangeLogs();
  }, [fetchRegistrationsCount, fetchClassChangeLogs]);

  const handleScheduleClick = (schedule, totalHours) => {
    const sortedSchedule = [...schedule].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) return dateA - dateB;
      const timeA = a.schedule_time ? a.schedule_time.split(":").map(Number) : [0, 0];
      const timeB = b.schedule_time ? b.schedule_time.split(":").map(Number) : [0, 0];
      return timeA[0] !== timeB[0] ? timeA[0] - timeB[0] : timeA[1] - timeB[1];
    });
    setPopupData(sortedSchedule);
    setPopupTotalHours(totalHours || 0);
  };

  const closePopup = () => {
    setPopupData(null);
    setPopupTotalHours(0);
  };

  const closeEditModal = () => {
    setEditData(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleSaveEdit = async () => {
    const token = localStorage.getItem('token');
    if (!editData.groupID) {
      console.error('groupID is undefined');
      return;
    }
    try {
      await axios.put(`${API_URL}/class-groups/${editData.groupID}`, editData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setEditData(null);
      fetchClassGroups(currentPage, searchTerm, privateFilter, startDateFilter);
      message.success(t('Class group updated successfully'));
    } catch (error) {
      console.error('Error updating class group:', error);
      message.error(t('Failed to update class group'));
    }
  };

  const handleEditClick = (classGroup) => {
    setEditData({
      groupID: classGroup.groupid,
      class_code: classGroup.class_code,
      book_details_materials: classGroup.book_details_materials,
      isprivate: classGroup.isprivate,
      register_capacity: classGroup.register_capacity,
      price: classGroup.price,
    });
  };

  const getRegisteredStudents = (groupId) => {
    const group = registrationsCount.find(rc => rc.groupid === groupId);
    return group ? group.registered_students : 0;
  };

  const getChangeReasonDisplay = (reason) => {
    switch (reason) {
        case 'admin_adjust':
            return { text: 'Admin', color: '#005181' };
        case 'cancelled_by_student':
            return { text: 'CL by S.', color: 'red' };
        case 'cancelled_by_teacher':
            return { text: 'CL by T.', color: 'orange' };
        case 'class_on_hold_by_student':
            return { text: 'On hold by S.', color: 'blue' };
        case 'class_on_hold_by_school':
            return { text: 'On hold by P.', color: 'green' };
        case 'cancel_same_day':
            return { text: 'Cancel Same Day', color: '#FF4500' };
        case 'make_up':
            return { text: 'Make Up', color: '#8A2BE2' };
        case 'others':
            return { text: 'Others', color: 'darkgrey' };
        default:
            return { text: reason, color: 'black' };
    }
  };

  const confirmDeleteClassGroup = (groupID) => {
    confirm({
      title: t('confirmDelete'),
      icon: <ExclamationCircleOutlined />,
      content: t('areYouSureToDelete'),
      okText: t('yes'),
      cancelText: t('no'),
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/class-groups/${groupID}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setClassGroups((prevClasses) => prevClasses.filter((cls) => cls.groupid !== groupID));
          message.success(t('Class group deleted successfully'));
        } catch (error) {
          console.error('Error deleting class group:', error);
          message.error(t('Failed to delete class group'));
        }
      }
    });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Debounce the actual API call by 500ms
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 500);
  };

  const handleDeleteClass = (classId) => {
    setClassIdToDelete(classId);
    setPassword("");
    setPasswordError("");
    setIsPasswordModalOpen(true);
  };
  
  const confirmDeleteClass = async () => {
    try {
      if (!password.trim()) {
        setPasswordError(t("Password is required"));
        return;
      }
  
      const token = localStorage.getItem("token");
      if (!token) {
        setError(t("noToken"));
        return;
      }
  
      try {
        await axios.delete(`${API_URL}/classes/${classIdToDelete}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          data: { password: password.trim() }
        });     
  
      setPopupData((prevData) =>
        prevData.filter((cls) => cls.classid !== classIdToDelete)
      );
  
      fetchClassGroups(currentPage, searchTerm, privateFilter, startDateFilter);
      setIsPasswordModalOpen(false);
      message.success(t('Class has been successfully deleted.'));
      
    } catch (error) {
      console.error("Delete request error:", error.response || error);
      
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setPasswordError(t("Password is required."));
            break;
          case 401:
            setPasswordError(t("Invalid password. Please try again."));
            break;
          case 404:
            setError(t("Class or user not found."));
            break;
          default:
            setError(t("Failed to delete class. Please try again later."));
        }
      } else {
        setError(t("Network error. Please check your connection."));
      }
    }
  } catch (error) {
    console.error("Error in confirmDeleteClass:", error);
    setError(t("An unexpected error occurred."));
  }
};
  
  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    loadSarabunFont(doc);
  
    const margin = 20;
    const startY = margin + 60;
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;
    const maxLineWidth = pageWidth - 40; 
  
    const headerImage = new Image();
    headerImage.src = '/images/Theplanner.png';
  
    headerImage.onload = () => {
      // Add header image
      doc.addImage(headerImage, 'PNG', margin, margin, 35, 30);
  
      // Add header text
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('THE PLANNER EDUCATION', margin + 50, margin + 10);
  
      doc.setFont('helvetica', 'normal');
      doc.text('The Mercury Ville @Chidlom 3rd floor', margin + 50, margin + 15);
      doc.text('Lumpini Pathumwan Bangkok', margin + 50, margin + 20);
      doc.text('www.theplannereducation.com', margin + 50, margin + 25);
      doc.text('Tel. 02-253-2533 or 095-726-2666 Line: @theplanner', margin + 50, margin + 30);
  
      // Prepare class details data
      const classCode = popupData[0]?.class_code || 'N/A';
      const totalHours = popupTotalHours;
      const subjects = [...new Set(popupData.map(slot => slot.subject_name))];
  
      // Add class code and total hours
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(classCode, centerX, startY - 20, { align: 'center' });
      doc.text(`Total Hours: ${formatMinutesToHours(totalHours)}`, centerX, startY - 15, { align: 'center' });
  
      // Handle long subject list with word wrapping
      doc.setFont('helvetica', 'normal');
      const subjectsText = subjects.join(', ');
      
      // Split long subject text into multiple lines
      const splitSubjects = doc.splitTextToSize(`Subject(s): ${subjectsText}`, maxLineWidth);
      
      // Calculate total height needed for subjects
      const lineHeight = 5;
      const totalSubjectsHeight = splitSubjects.length * lineHeight;
      
      // Add each line of subjects text
      splitSubjects.forEach((line, index) => {
        doc.text(line, centerX, startY - 10 + (index * lineHeight), { align: 'center' });
      });
  
      // Adjust table starting position based on number of subject lines
      const tableStartY = startY + totalSubjectsHeight;
  
      // Sort popupData
      const sortedPopupData = popupData.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (dateA.getTime() === dateB.getTime()) {
          return a.schedule_time.localeCompare(b.schedule_time);
        }
        return dateA - dateB;
      });
  
      // Prepare table data
      const columns = [
        { header: 'Day', dataKey: 'schedule_day', width: 25 },
        { header: 'Date', dataKey: 'date', width: 25 },
        { header: 'Subject', dataKey: 'subject_name', width: 45 },
        { header: 'Start Time', dataKey: 'schedule_time', width: 30 },
        { header: 'End Time', dataKey: 'end_time', width: 30 },
        { header: 'Hours', dataKey: 'schedule_hour', width: 20 },
        { header: 'Tutor', dataKey: 'nickname', width: 25 },
        { header: 'Room', dataKey: 'classroom_number', width: 20 },
      ];
      
      const rows = sortedPopupData.map(slot => {
        const changeLog = classChangeLogs.find(ccl => ccl.classid === slot.classid);
        const reasonDisplay = changeLog
          ? getChangeReasonDisplay(changeLog.changereason) 
          : { text: '-', color: 'black' };

        return {
          schedule_day: getDayOfWeek(slot.date),
          date: formatDate(slot.date),
          subject_name: slot.subject_name,
          schedule_time: formatTime(slot.schedule_time), 
          end_time: formatTime(slot.end_time),
          nickname: slot.nickname,
          schedule_hour: formatScheduleHour(slot.schedule_hour),
          classroom_number: slot.classroom_number,
        };
      });
         
      // Add table with adjusted column widths and word wrap
      doc.autoTable({
        startY: tableStartY,
        head: [columns.map(col => col.header)],
        body: rows.map(row => columns.map(col => row[col.dataKey])),
        theme: 'plain',
        styles: {
          font: 'Sarabun',
          fontStyle: 'normal',
          cellPadding: 2,
          overflow: 'linebreak',
          fontSize: 9,
          lineWidth: 0,
        },
        columnStyles: {
          subject_name: { cellWidth: 45 },
          schedule_time: { cellWidth: 30 },
          schedule_day: { cellWidth: 25 },
          date: { cellWidth: 25 },
          nickname: { cellWidth: 25 },
          schedule_hour: { cellWidth: 20 },
          classroom_number: { cellWidth: 20 }
        },
        headStyles: {
          fillColor: '#005082',
          textColor: '#FFFFFF',
          fontSize: 10,
          fontStyle: 'bold',
          halign: 'left',
          valign: 'middle',
          lineWidth: 0
        },
        alternateRowStyles: {
          fillColor: '#F5F5F5'
        },
        bodyStyles: {
          lineWidth: 0,
          textColor: '#333333',
          fontSize: 9,
          halign: 'left',
          valign: 'middle',
          fillColor: '#FFFFFF'
        },
        margin: { left: 10, right: 10 },
        tableWidth: 'auto',
        didParseCell: function(data) {
          if (data.cell.raw === null || data.cell.raw === undefined) {
            data.cell.text = '-';
          }
        },
        rowSpacing: 1,
      });
  
      // Save the PDF
      doc.save(`${classCode}.pdf`);
    };
  
    headerImage.onerror = () => {
      console.error('Failed to load the header image.');
    };
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="ant-spin ant-spin-lg ant-spin-spinning">
          <span className="ant-spin-dot ant-spin-dot-spin">
            <i className="ant-spin-dot-item"></i>
            <i className="ant-spin-dot-item"></i>
            <i className="ant-spin-dot-item"></i>
            <i className="ant-spin-dot-item"></i>
          </span>
        </div>
        <div className="ant-spin-text">{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  // Data is already filtered, sorted, and paginated by the server

  // Table columns configuration
  const columns = [
    {
      title: t('classCode'),
      dataIndex: 'class_code',
      key: 'class_code',
      width: 450,
      ellipsis: false,
      render: (text, record) => (
        <Link to={`/classdetails/${record.groupid}`} style={{ textDecoration: 'none', fontWeight: 'bold', whiteSpace: 'normal',
          overflowWrap: 'break-word', maxWidth: '100%', display: 'inline-block' }}>
          {text}
        </Link>
      )
    },
    {
        title: t('schedule'),
        key: 'schedule',
        ellipsis: false,
        width: 140, // set fixed width
        render: (_, record) => (
          <div style={{ whiteSpace: 'nowrap' }}>
            <Button 
              type="primary" 
              icon={<ScheduleOutlined />} 
              size="small"
              onClick={() => handleScheduleClick(record.classes, record.total_hours)}
            >
              {t('schedule')}
            </Button>
          </div>
        )
      },
           {
            title: t('classes'),
            key: 'classes',
            render: (_, record) => (
              <Link to={`/course/${record.groupid}`}>
                <Button 
                  icon={<ScheduleOutlined />}
                  type="default"
                  style={{ width: '50px' }}
                >
                </Button>
              </Link>
            ),
          },
          {
        title: t('startDate'),
        dataIndex: 'start_date',
        key: 'start_date',
        render: (date) => (
          <div style={{ whiteSpace: 'nowrap' }}>
            {formatDate(date)}
          </div>
        ),
      },

      {
        title: t('endDate'),
        dataIndex: 'end_date',
        key: 'end_date',
        render: (date) => (
          <div style={{ whiteSpace: 'nowrap' }}>
            {formatDate(date)}
          </div>
        ),
      },
    {
      title: t('totalHours'),
      dataIndex: 'total_hours',
      key: 'total_hours',
      render: (hours) => formatMinutesToHours(hours)
    },
   {
  title: t('Notes'),
  dataIndex: 'book_details_materials',
  key: 'notes',
  width: 200,
  ellipsis: false,
  render: (text) => (
    <div
      style={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        maxWidth: 200,
      }}
    >
      {text}
    </div>
  )
},
    {
      title: t('private'),
      dataIndex: 'isprivate',
      key: 'isprivate',
      render: (isprivate) => (
        isprivate ? 
          <Tag color="blue">{t('private')}</Tag> : 
          <Tag color="green">{t('group')}</Tag>
      )
    },
    {
      title: t('capacity'),
      dataIndex: 'register_capacity',
      key: 'capacity',
    },
    {
      title: t('totalRegis'),
      key: 'registrations',
      render: (_, record) => getRegisteredStudents(record.groupid)
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {getRegisteredStudents(record.groupid) < 1 && (
            <Tooltip title={t('delete')}>
              <Button 
                danger 
                icon={<DeleteOutlined />} 
                size="small" 
                onClick={() => confirmDeleteClassGroup(record.groupid)}
              />
            </Tooltip>
          )}
          <Tooltip title={t('edit')}>
            <Button 
              icon={<EditOutlined />} 
              size="small" 
              onClick={() => handleEditClick(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  // Schedule table columns for the modal
  const scheduleColumns = [
    {
      title: t('day'),
      dataIndex: 'date',
      key: 'day',
      render: (date) => getDayOfWeek(date)
    },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      render: (date) => formatDate(date)
    },
    {
      title: t('subjectName'),
      dataIndex: 'subject_name',
      key: 'subject'
    },
    {
      title: t('time'),
      key: 'time',
      render: (_, record) => `${formatTime(record.schedule_time)} - ${formatTime(record.end_time)}`
    },
    {
      title: t('teacherName'),
      dataIndex: 'nickname',
      key: 'teacher'
    },
    {
      title: t('hours'),
      dataIndex: 'schedule_hour',
      key: 'hours',
      render: (hour) => formatScheduleHour(hour)
    },
    {
      title: t('classroomNumber'),
      dataIndex: 'classroom_number',
      key: 'classroom'
    },
    {
      title: t('classchange'),
      key: 'classchange',
      render: (_, record) => {
        const changeLog = classChangeLogs.find(ccl => ccl.classid === record.classid);
        const changeReasonDisplay = changeLog
          ? getChangeReasonDisplay(changeLog.changereason)
          : { text: "-", color: "black" };
        
        return <span style={{ color: changeReasonDisplay.color }}>{changeReasonDisplay.text}</span>;
      }
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Button 
          danger 
          icon={<DeleteOutlined />} 
          size="small" 
          onClick={() => handleDeleteClass(record.classid)}
        />
      )
    }
  ];

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="manage-course-container">
        <Card title="Courses">
            <div className="filters-container" style={{ marginBottom: '24px' }}>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '16px', 
                alignItems: 'flex-end',
                background: '#f5f5f5', 
                padding: '16px', 
                borderRadius: '8px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}>
                {/* Text Search */}
                <div style={{ flex: '2 1 300px' }}>
                  <div style={{ marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                    {t('Search by Class Code')}
                  </div>
                  <Input
                    placeholder={t('Enter class code...')}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    allowClear
                    style={{ width: '100%' }}
                    prefix={<SearchOutlined />}
                  />
                </div>
                
                {/* Private/Group Filter */}
                <div style={{ flex: '1 1 150px' }}>
                  <div style={{ marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                    {t('Class Type')}
                  </div>
                  <Select
                    value={privateFilter}
                    onChange={(value) => { setPrivateFilter(value); setCurrentPage(1); }}
                    style={{ width: '100%' }}
                    dropdownMatchSelectWidth={false}
                  >
                    <Option value="all">{t('All Class')}</Option>
                    <Option value="private">{t('Private')}</Option>
                    <Option value="group">{t('Group')}</Option>
                  </Select>
                </div>
                
                {/* Date Filter */}
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                    {t('Start Date')}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="date"
                      onChange={(e) => {
                        const date = e.target.value ? new Date(e.target.value) : null;
                        setStartDateFilter(date);
                        setCurrentPage(1);
                      }}
                      style={{ 
                        flex: 1,
                        height: '22px', 
                        padding: '4px 11px',
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9',
                        backgroundColor: 'white'
                      }}
                    />
                    {startDateFilter && (
                      <Button 
                        icon={<CloseCircleOutlined />}
                        onClick={() => setStartDateFilter(null)}
                        size="middle"
                      />
                    )}
                  </div>
                </div>
                
                {/* Reset Filters Button */}
                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                  <Button 
                    onClick={() => {
                      setSearchTerm('');
                      setDebouncedSearch('');
                      setPrivateFilter('all');
                      setStartDateFilter(null);
                      setCurrentPage(1);
                      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                    }}
                    icon={<ReloadOutlined />}
                  >
                    {t('Reset Filters')}
                  </Button>
                </div>
              </div>
            </div>
            <div> <style>
          {`
            .ant-table-tbody > tr:hover > td {
              background-color: #ffebee !important;
              cursor: pointer;
            }
          `}
        </style>
          <Table
            columns={columns}
            dataSource={classGroups}
            rowKey="groupid"
            pagination={{
              current: currentPage,
              pageSize: itemsPerPage,
              total: totalItems,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false
            }}
            size="large"
            bordered
            scroll={{ x: 'max-content' }}
            style={{ overflowX: 'auto', marginTop: '20px' }}
          />
          </div>
        </Card>

        {/* Schedule Details Modal */}
<AntModal
  title={popupData?.[0]?.class_code || t('scheduleDetails')}
  open={!!popupData}
  onCancel={closePopup}
  footer={[
    <Button key="download" onClick={handleDownloadPDF} icon={<DownloadOutlined />}>
      {t('downloadPDF')}
    </Button>,
    <Button key="close" onClick={closePopup}>
      {t('close')}
    </Button>
  ]}
  width="90%"
  style={{ top: 20 }}
  bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
>
  {popupData && (
   <Table
      columns={scheduleColumns}
      dataSource={popupData}
      rowKey="classid"
      pagination={false}
      size="small"
      scroll={{ x: 'max-content' }}
      rowClassName="custom-row"
    />
  )}
</AntModal>

      {/* Edit Class Group Modal */}
<AntModal
  title={t('editClassGroup')}
  open={!!editData}
  onCancel={closeEditModal}
  footer={[
    <Button key="cancel" onClick={closeEditModal}>
      {t('cancel')}
    </Button>,
    <Button key="save" type="primary" onClick={handleSaveEdit}>
      {t('confirm')}
    </Button>
  ]}
  width={600}
>
  {editData && (
    <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          {t('classCode')}:
        </label>
        <Input 
          type="text" 
          name="class_code" 
          value={editData.class_code} 
          onChange={handleEditChange} 
        />
      </div>
      
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          {t('Notes')}:
        </label>
        <Input 
          type="text" 
          name="book_details_materials" 
          value={editData.book_details_materials} 
          onChange={handleEditChange} 
        />
      </div>
      
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          {t('private')}:
        </label>
        <Select 
          name="isprivate" 
          value={editData.isprivate}
          onChange={(value) => setEditData({ ...editData, isprivate: value })}
          style={{ width: '100%' }}
        >
          <Option value={true}>{t('private')}</Option>
          <Option value={false}>{t('group')}</Option>
        </Select>
      </div>
      
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          {t('registerCapacity')}:
        </label>
        <Input 
          type="number" 
          name="register_capacity" 
          value={editData.register_capacity} 
          onChange={handleEditChange} 
        />
      </div>
      
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          {t('price')}:
        </label>
        <Input 
          type="number" 
          name="price" 
          value={editData.price} 
          onChange={handleEditChange} 
        />
      </div>
    </form>
  )}
</AntModal>

        {/* Delete Confirmation Modal */}
        <AntModal
          title={<Text type="danger">{t("Dangerous! This action cannot be reversed.")}</Text>}
          open={isPasswordModalOpen}
          onCancel={() => setIsPasswordModalOpen(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsPasswordModalOpen(false)}>
              {t("Cancel")}
            </Button>,
            <Button key="delete" danger type="primary" onClick={confirmDeleteClass}>
              {t("Delete")}
            </Button>
          ]}
        >
          <div>
            <p>
              {t(
                "Deleting a class cannot be undone! You also CANNOT add a class into any course later. Ensure you want to delete this class."
              )}
            </p>
            {passwordError && <p style={{ color: "red" }}>{passwordError}</p>}
            <Input.Password
              placeholder={t("Enter your password to confirm")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ marginBottom: "16px" }}
            />
          </div>
        </AntModal>
      </div>
    </div>
  );
};

export default withRoleAccess(FilterTaskTable, ['admin', 'superadmin']);